import logging from 'app/shared/utils/logging';
import reactPlugin from 'app/shared/utils/appInsightsPlugin';
import { encryptObject } from './aesGcmEncryption';

type BridgeMessage = {
    data: string;
};

export type WindowWithBridge = Window & {
    platform?: string;
    eventsHandler?: {
        postMessage: (message: string) => void;
    };
    webkit?: {
        messageHandlers?: {
            eventsController?: {
                postMessage: (message: BridgeMessage) => void;
            };
        };
    };
};

export type PageTitleInfo = {
    showBackButton: boolean;
    rightBarButton?: {
        btnTitle: string;
        btnAction: string;
    };
    title: string;
    subTitle?: string;
};

const buildBridgeMessage = async (
    payload: Record<string, unknown>,
    encrypt = false,
): Promise<string | null> => {
    if (!encrypt) {
        return JSON.stringify(payload);
    }

    return encryptObject(payload);
};

const BRIDGE_EVENT_NAMESPACE = 'BRIDGE';

// logging.* stringifies details for App Insights; Errors serialize to "{}" on
// their own, so surface the message instead of losing it.
const serializeDetails = (details?: Record<string, unknown>) => {
    try {
        if (!details) {
            return details;
        }
        return Object.fromEntries(
            Object.entries(details).map(([key, value]) => [
                key,
                value instanceof Error ? value.message : value,
            ]),
        );
    } catch (e) {
        logging.error(reactPlugin, BRIDGE_EVENT_NAMESPACE, 'ObjectSerializationError', {});
    }
    return {};
};

const logBridgeMessage = (
    level: 'INFO' | 'WARN' | 'ERROR',
    message: string,
    details?: Record<string, unknown>,
) => {
    const text = `[${level}] ${message}`;
    const payload = serializeDetails(details);

    if (level === 'ERROR') {
        logging.error(reactPlugin, BRIDGE_EVENT_NAMESPACE, text, payload);
    } else if (level === 'WARN') {
        logging.warn(reactPlugin, BRIDGE_EVENT_NAMESPACE, text, payload);
    } else {
        logging.info(reactPlugin, BRIDGE_EVENT_NAMESPACE, text, payload);
    }
};

async function publishBridgeEvent(payload: Record<string, unknown>, encrypt = false) {
    const windowWithBridge = window as WindowWithBridge;
    const platform = windowWithBridge.platform?.toLowerCase() ?? 'unknown';

    try {
        const data = await buildBridgeMessage(payload, encrypt);

        if (!data) {
            logBridgeMessage('ERROR', 'Event message was empty; event was not sent.');
            return;
        }

        logBridgeMessage('INFO', 'Publishing event.', { event: payload.event, platform });

        if (platform === 'android') {
            const handler = windowWithBridge.eventsHandler;
            if (!handler) {
                logBridgeMessage('ERROR', 'Android event handler is unavailable.', { event: payload.event });
                return;
            }
            handler.postMessage(JSON.stringify({ data }));
            return;
        }
        if (platform === 'ios') {
            const handler = windowWithBridge.webkit?.messageHandlers?.eventsController;
            if (!handler) {
                logBridgeMessage('ERROR', 'iOS event handler is unavailable.', { event: payload.event });
                return;
            }
            handler.postMessage({ data });
        }
    } catch (error) {
        logBridgeMessage('ERROR', 'Failed to publish event.', { event: payload.event, platform, error });
        throw error;
    }
}

async function publishRouteChangeEvent(event: string, nextRoute: string) {
    const message = { event, nextRoute };
    await publishBridgeEvent(message);
}

async function publishApplePayEvent(event: string, orderNumber: string, orderAmount: number) {
    const message = { event, orderNumber, orderAmount };
    await publishBridgeEvent(message);
}

const ignoreBridgePublishError = () => undefined;

function toggleTopbarActions(isEnabled: boolean) {
    const event = `${isEnabled ? 'enable' : 'disable'}ToolbarActions`;
    publishBridgeEvent({ event }).catch(ignoreBridgePublishError);
}

export function publishTopBarUpdate(payload: PageTitleInfo) {
    publishBridgeEvent({ event: 'mobile-topbarUpdate', ...payload }).catch(ignoreBridgePublishError);
}

export function publishShellEvent(event: string) {
    publishBridgeEvent({ event }).catch(ignoreBridgePublishError);
}

export {
    publishBridgeEvent,
    publishApplePayEvent,
    publishRouteChangeEvent,
    toggleTopbarActions,
};
